const READ_MORE_SELECTOR = ".read-more[data-read-more]";
const initializedWraps = new WeakSet();
let globalListenersBound = false;
let resizeTimer = null;

function getLines(wrap) {
  const fromAttr = parseInt(wrap.getAttribute("data-read-more-lines") || "", 10);
  if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;

  const fromCss = parseInt(
    getComputedStyle(wrap).getPropertyValue("--read-more-lines").trim(),
    10
  );
  if (Number.isFinite(fromCss) && fromCss > 0) return fromCss;

  return 2;
}

function setExpanded(wrap, expanded) {
  const content = wrap.querySelector("[data-read-more-content]");
  const toggle = wrap.querySelector("[data-read-more-toggle]");
  const moreLabel = wrap.querySelector("[data-read-more-more]");
  const lessLabel = wrap.querySelector("[data-read-more-less]");

  if (!content || !toggle) return;

  content.classList.toggle("read-more__content--collapsed", !expanded);
  toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
  toggle.classList.toggle("read-more__toggle--expanded", expanded);

  if (moreLabel) moreLabel.hidden = expanded;
  if (lessLabel) lessLabel.hidden = !expanded;

  wrap.dispatchEvent(
    new CustomEvent("read-more:change", {
      bubbles: true,
      detail: { expanded },
    })
  );
}

function contentOverflows(content, lines) {
  if (!content || !content.parentElement) return false;

  const width = content.offsetWidth;
  if (!width) return false;

  const full = content.cloneNode(true);
  full.removeAttribute("id");
  full.setAttribute("aria-hidden", "true");
  full.classList.remove("read-more__content--collapsed");
  full.style.cssText =
    "position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:" +
    width +
    "px;display:block;-webkit-line-clamp:unset;max-height:none;overflow:visible;height:auto;";

  const clamped = content.cloneNode(true);
  clamped.removeAttribute("id");
  clamped.setAttribute("aria-hidden", "true");
  clamped.classList.add("read-more__content--collapsed");
  clamped.style.cssText =
    "position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:" +
    width +
    "px;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:" +
    lines +
    ";overflow:hidden;height:auto;max-height:none;";

  content.parentElement.appendChild(full);
  content.parentElement.appendChild(clamped);
  const overflows = full.scrollHeight > clamped.clientHeight + 1;
  content.parentElement.removeChild(full);
  content.parentElement.removeChild(clamped);

  return overflows;
}

function updateWrap(wrap) {
  const content = wrap.querySelector("[data-read-more-content]");
  const toggle = wrap.querySelector("[data-read-more-toggle]");
  if (!content || !toggle) return;

  const lines = getLines(wrap);
  wrap.style.setProperty("--read-more-lines", String(lines));

  const isExpanded = toggle.getAttribute("aria-expanded") === "true";
  const needsToggle = contentOverflows(content, lines);

  if (!needsToggle) {
    toggle.hidden = true;
    content.classList.remove("read-more__content--collapsed");
    toggle.setAttribute("aria-expanded", "true");
    toggle.classList.remove("read-more__toggle--expanded");
    const moreLabel = wrap.querySelector("[data-read-more-more]");
    const lessLabel = wrap.querySelector("[data-read-more-less]");
    if (moreLabel) moreLabel.hidden = false;
    if (lessLabel) lessLabel.hidden = true;
    return;
  }

  toggle.hidden = false;
  if (isExpanded) {
    content.classList.remove("read-more__content--collapsed");
    return;
  }

  if (!content.classList.contains("read-more__content--collapsed")) {
    setExpanded(wrap, false);
  } else {
    content.classList.add("read-more__content--collapsed");
    toggle.setAttribute("aria-expanded", "false");
    toggle.classList.remove("read-more__toggle--expanded");
  }
}

function onToggleClick(event) {
  const toggle = event.currentTarget;
  const wrap = toggle.closest(READ_MORE_SELECTOR);
  if (!wrap) return;

  const isExpanded = toggle.getAttribute("aria-expanded") === "true";
  setExpanded(wrap, !isExpanded);
}

function initWrap(wrap) {
  if (!(wrap instanceof HTMLElement)) return;

  const toggle = wrap.querySelector("[data-read-more-toggle]");
  if (!toggle) return;

  if (!initializedWraps.has(wrap)) {
    toggle.addEventListener("click", onToggleClick);
    initializedWraps.add(wrap);
  }

  requestAnimationFrame(() => updateWrap(wrap));
}

function initAll(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  scope.querySelectorAll(READ_MORE_SELECTOR).forEach(initWrap);
}

function bindGlobalListeners() {
  if (globalListenersBound) return;
  globalListenersBound = true;

  window.addEventListener(
    "resize",
    () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        document.querySelectorAll(READ_MORE_SELECTOR).forEach(updateWrap);
      }, 150);
    },
    { passive: true }
  );

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
}

function boot() {
  bindGlobalListeners();
  initAll();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}

export { initAll as initReadMore, updateWrap as updateReadMore };
